<template>
  <header class="mb-3">
    <div class="cookie-message pa-4" v-if="!cookieAccepted" :style="`background-color: ${ goDark ? '#424242' : 'white' }`">
      {{ $lang.header.cookie_1 }} <a @click.prevent.stop="inNewTab('data-protection')"
                                     class="primary--text">{{ $lang.header.cookiePrivacy }}</a>.
      {{ $lang.header.cookie_2 }}.
      <v-btn class="primary elevation-0" @click="acceptCookies()">{{ $lang.header.accept }}</v-btn>
    </div>
    <v-layout row justify-center>
      <v-flex xs12 :pl-4="$vuetify.breakpoint.lgAndUp" style="max-width: 1440px" :class="{'header-move': backlink && $vuetify.breakpoint.smAndUp}">
        <v-layout row wrap justify-space-between>
          <v-flex xs12 v-if="backlink && $vuetify.breakpoint.smAndUp" class="backlink-style" :pl-4="$vuetify.breakpoint.lgAndUp">
            <v-layout row wrap fill-height align-center justify-center style="max-width: 1440px; margin: auto">
              <v-flex grow style="max-width: 1440px; padding-left: 24px">
                <h4 class="primary-text-color"><a :href="backlinkUrl"><v-icon color="#efa546" size="16" class="pr-2">keyboard_arrow_left</v-icon>{{ $lang.global.backTo }} {{backlink.toUpperCase()}}</a></h4>
              </v-flex>
            </v-layout>
          </v-flex>
          <v-flex class="text-xs-center text-sm-right actions" order-xs0 order-sm1 v-if="companyActive !== false" style="max-width: 100vw">
            <v-layout row wrap :justify-space-between="backlink && $vuetify.breakpoint.xsOnly" :justify-center="!backlink && $vuetify.breakpoint.smAndDown" :justify-end="$vuetify.breakpoint.mdAndUp">
              <div class="d-inline-flex align-center text-xs-right" v-if="backlink && $vuetify.breakpoint.xsOnly">
                <span class="primary-text-color"><a style="line-height: 24px" :href="backlinkUrl"><v-icon color="#efa546" size="16" class="pr-2">keyboard_arrow_left</v-icon>{{ ($lang.actions.back).toUpperCase() }}</a></span>
              </div>
              <div class="d-inline-flex align-center text-xs-right" style="max-width: 100vw">
                <v-btn id="chat" class="hidden-on-phone" flat small color="primary ma-0" v-if="intercomVisible"
                       @click="activateIntercom">
                  <v-icon>chat_bubble_outline</v-icon>&nbsp; {{ $lang.labels.chat }}
                </v-btn>
                <v-btn id="visits" flat small color="primary ma-0" v-if="isLoggedIn && isAdmin" @click="toVisits">
                  <v-icon class="mr-1">admin_panel_settings</v-icon>
                  {{ $lang.header.besuche }}
                </v-btn>
                <v-btn id="orders" flat small color="primary ma-0" v-if="isLoggedIn && isAdmin" @click="toOrders">
                  <v-icon class="mr-1">admin_panel_settings</v-icon>
                  {{ $lang.header.orders }}
                </v-btn>
                <v-btn id="register" flat small color="primary ma-0"
                       v-if="!isLoggedIn && $route.name !== 'registration' && $route.name !== 'ContactTracking'"
                       @click="toRegistration">
                  <v-icon class="mr-1">person_outline</v-icon>
                  {{ $lang.header.register }}
                </v-btn>
                <v-btn id="orderHistory" flat small color="primary ma-0"
                       v-if="isLoggedIn && $route.name !== 'registration' && $route.name !== 'login'"
                       @click="$router.push({ name: 'orderHistory' })">
                  <v-icon class="mr-1">list</v-icon>
                  {{ $lang.header.history }}
                </v-btn>
                <v-btn id="settings" flat small color="primary ma-0"
                       v-if="isLoggedIn && $route.name !== 'registration' && $route.name !== 'login'" @click="toAddresses">
                  <v-icon class="mr-1">settings</v-icon>
                  {{ $lang.header.settings }}
                </v-btn>
                <v-btn flat small color="primary ma-0 ml-4" v-if="isLoggedIn && $route.name !== 'login'" @click="logout">
                  <v-icon class="mr-1">exit_to_app</v-icon>
                  {{ $lang.header.logout }}
                </v-btn>
                <v-btn id="login" flat small color="primary ma-0 ml-4"
                       v-else-if="!isLoggedIn && $route.name !== 'login' && $route.name !== 'ContactTracking'"
                       @click="toLogin">
                  <v-icon class="mr-1">person</v-icon>
                  {{ $lang.header.login }}
                </v-btn>
              </div>
            </v-layout>
          </v-flex>
          <v-layout row wrap class="header-image">
            <v-img v-if="headerImage" contain class="header-image" :src="headerImage" style="max-height: 350px; width: 90vw; margin: auto"></v-img>
          </v-layout>
          <v-layout row wrap justify-center v-if="company" class="mobile-card px-2" style="margin-top: 10px">
            <v-card id="headerCard" class="pa-4" style="min-height: 140px">
              <v-layout row class="mb-3 mb-sm-0 layout-fix">
                <img v-if="logoImage" :src="logoImage" style="max-height: 120px;max-width: 120px; background-position: unset;">
                <div class="rest-desc text-xs-left pl-4">
                  <h3 @click="$router.push({ name: 'webShop' })" class="mb-2">{{ company.name.toUpperCase() }}</h3>
                  <p class="mb-0">
                    {{
                      generateAddress([
                        company.address.line1,
                        company.address.line2,
                        company.address.line3,
                        company.address.line4,
                      ])
                    }}</p>
                  <p class="mb-0">
                    {{ company.address.locality ? company.address.locality + ', ' : '' }}
                    {{ company.address.country === 'UK' ? company.address.townCity : company.address.postalCode }},
                    {{ company.address.country === 'UK' ? company.address.postalCode : company.address.townCity }}
                  </p>
                </div>
              </v-layout>
              <v-layout column justify-end class="mb-3 mb-sm-0 phone-layout-fix">
                <a v-if="company.phone"
                   class="hidden-on-phone"
                   :href="`tel:${company.phone}`"
                   style="text-decoration: none; font-size: 16px;">
                  <v-icon class="mr-2" color="primary">phone</v-icon>
                  {{ company.phone }}
                </a>
                <div
                    v-if="company.email"
                    style="font-size: 16px;"
                    class="hidden-on-phone">
                  <v-icon class="mr-2" color="primary">email</v-icon>
                  {{ company.email }}
                </div>
              </v-layout>
            </v-card>
          </v-layout>
          <v-layout row wrap justify-space-between v-if="company" class="desktop-card">
              <v-layout row class="mb-3 mb-sm-0 layout-fix">
                <img v-if="logoImage" :src="logoImage" style="max-height: 90px;max-width: 350px;" class="mr-4">
                <div class="rest-desc">
                  <h2 @click="$router.push({ name: 'webShop' })">{{ company.name }}</h2>
                  <p class="mb-0">
                    {{
                      generateAddress([
                        company.address.line1,
                        company.address.line2,
                        company.address.line3,
                        company.address.line4,
                      ])
                    }}</p>
                  <p class="mb-0">
                    {{ company.address.locality ? company.address.locality + ', ' : '' }}
                    {{ company.address.country === 'UK' ? company.address.townCity : company.address.postalCode }},
                    {{ company.address.country === 'UK' ? company.address.postalCode : company.address.townCity }}
                  </p>
                </div>
              </v-layout>
              <v-layout column justify-end class="mb-3 mb-sm-0 phone-layout-fix">
                <a v-if="company.phone"
                   class="hidden-on-phone"
                   :href="`tel:${company.phone}`"
                   style="text-decoration: none; font-size: 16px;">
                  <v-icon class="mr-2" color="primary">phone</v-icon>
                  {{ company.phone }}
                </a>
                <div
                    v-if="company.email"
                    style="font-size: 16px;"
                    class="hidden-on-phone">
                  <v-icon class="mr-2" color="primary">email</v-icon>
                  {{ company.email }}
                </div>
              </v-layout>
          </v-layout>
        </v-layout>
        <v-layout row>
          <v-flex xs12>
            <v-alert
                color="success"
                class="success"
                icon="check"
                value="true"
                v-if="success">{{ success }}
            </v-alert>
            <v-alert
                color="error"
                class="err"
                icon="warning"
                value="true"
                v-if="err">{{ err }}
            </v-alert>
            <v-alert
                color="error"
                class="err"
                icon="warning"
                value="true"
                v-if="company && company.webShopResourceStatus !== 'active'">{{ $lang.header.notActive }}
            </v-alert>

          </v-flex>
        </v-layout>
      </v-flex>
    </v-layout>
  </header>
</template>
<script>
import moment from 'moment';
import { mapGetters } from 'vuex';
import auth from '../../auth';
import { appId } from '../../const';

export default {
  data: () => ({
    isLoggedIn: auth.isLoggedIn(),
    isAdmin: auth.isAdmin(),
    success: '',
    err: '',
    cookieAccepted: localStorage.getItem('cookiesAccepted'),
    language: localStorage.getItem('lang') || 'en',
    backlink: false,
    backlinkUrl: '',
    headerImageHeight: 0,
  }),
  computed: {
    ...mapGetters({
      company: 'company',
      intercomVisible: 'intercomVisible',
      companyActive: 'companyActive',
      goDark: 'goDark',
      width: 'width',
    }),
    logoImage() {
      if (this.company && this.company.htmlHints && this.company.htmlHints.find(x => x.name === 'webshop.logo')) { return this.company.htmlHints.find(x => x.name === 'webshop.logo').value; }
      return false;
    },
    headerImage() {
      if (this.company && this.company.htmlHints && this.company.htmlHints.find(x => x.name === 'webshop.headerImage.mobile')) {
        return this.company.htmlHints.find(x => x.name === 'webshop.headerImage.mobile').value;
      }
      return false;
    },
  },
  created() {
    this.$lang.setLang(this.language);
    if (this.$route.params.lang !== this.$lang.current_lang) {
      if (['en', 'de'].includes(this.$route.params.lang)) {
        this.$lang.setLang(this.$route.params.lang);
        localStorage.setItem('lang', this.$route.params.lang);
      } else {
        this.$router.push('/en');
      }
    }
    this.backlinkCheck();
  },
  methods: {
    backlinkCheck() {
      const backlink = localStorage.backlink ? JSON.parse(localStorage.backlink) : false;
      const ordercompletedlink = localStorage.ordercompletedlink ?
        JSON.parse(localStorage.ordercompletedlink) : false;
      const tokenId = localStorage.tokenId ?
        JSON.parse(localStorage.tokenId) : false;
      if (backlink) {
        if (moment().toISOString() > moment(backlink.deleteTime).toISOString()) {
          localStorage.removeItem('backlink');
          return;
        }
        this.backlinkUrl = backlink.backlink;
        const el = document.createElement('a');
        el.href = backlink.backlink;
        const hostnameArr = el.hostname.split('.');
        if (hostnameArr.length < 3) this.backlink = hostnameArr[0];
        if (hostnameArr.length >= 3) this.backlink = hostnameArr[1];
      }
      if (ordercompletedlink) {
        if (moment().toISOString() > moment(ordercompletedlink.deleteTime).toISOString()) {
          localStorage.removeItem('ordercompletedlink');
        }
      }
      if (tokenId) {
        if (moment().toISOString() > moment(tokenId.deleteTime).toISOString()) {
          localStorage.removeItem('tokenId');
        }
      }
    },
    inNewTab(name) {
      const routeData = this.$router.resolve({ name, params: { lang: this.$lang.current_lang } });
      window.open(routeData.href, '_blank');
    },
    acceptCookies() {
      localStorage.setItem('cookiesAccepted', true);
      this.cookieAccepted = true;
    },
    toAddresses() {
      this.$router.push(`/${this.$lang.current_lang}/addresses`);
    },
    toRegistration() {
      this.$router.push(`/${this.$lang.current_lang}/registration`);
    },
    toVisits() {
      this.$router.push(`/${this.$lang.current_lang}/visits`);
    },
    toOrders() {
      this.$router.push(`/${this.$lang.current_lang}/orders`);
    },
    toLogin() {
      this.$router.push(`/${this.$lang.current_lang}/login`);
    },
    generateAddress(lines) {
      return lines.filter(line => !!line)
        .join(', ');
    },
    activateIntercom() {
      window.Intercom('show');
    },
    logout(showAlert = true, force = false) {
      auth.logout(this, force)
        .then((res) => {
          if (!res) {
            this.err = this.$lang.header.failLogOut;
            return;
          }
          this.isLoggedIn = false;
          window.Intercom('shutdown');
          if (showAlert) {
            window.Intercom('boot', {
              app_id: appId,
            });
            this.success = this.$lang.header.logOut;
            this.$router.push({ name: 'webShop' });
            window.location.reload(true);
            setTimeout(() => {
              this.success = '';
            }, 3000);
          }
        });
    },
  },
  watch: {
    $route: {
      handler(val) {
        if (
          val.params.resource !== localStorage.getItem('webShopResourceId') &&
            (val.name === 'resourceSet' || val.name === 'resourceSet2') &&
            !val.query.nologout &&
            auth.isLoggedIn()
        ) {
          this.logout(false, true);
          return;
        }
        this.isLoggedIn = auth.isLoggedIn();
        this.isAdmin = auth.isAdmin();
        if (val.query.nologout) {
          this.$router.push({ name: 'webShop' });
        }
        this.backlinkCheck();
      },
      immediate: true,
    },
  },
};
</script>
<style lang="scss" scoped>
.hidden-on-phone {
  @media only screen and (max-width: 600px) {
    display: none;
  }
}

.layout-fix {
  @media only screen and (max-width: 959px) {
    margin-bottom: 0 !important;
  }
}

.phone-layout-fix {
  @media only screen and (max-width: 959px) {
    width: 100%;
    text-align: left;
  }
  @media only screen and (max-width: 600px) {
    text-align: center;
  }
}

h2 {
  font-family: 'Roboto', cursive;
  font-weight: 300;
  font-size: 32px;
  cursor: pointer;
}

h3 {
  font-family: 'Roboto', cursive;
  font-weight: 500;
  font-size: 16px;
  line-height: 16px;
  cursor: pointer;
}

p {
  font-size: 14px;
}

.restaurant {
  max-width: 150px;
  max-height: 150px;
}

.web-shop {
  max-width: 230px;
  max-height: 150px;
}

.logo {
  position: relative;
  display: flex;
  flex: 1 1 0;
  width: 100%;
  height: auto;
  cursor: pointer;

  img {
    width: 100%;
    height: auto;
    object-fit: cover;
  }

  &.restaurant {
    cursor: pointer;
  }
}

.rest-desc {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  text-align: left;
}

.cookie-message {
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  z-index: 10000;

  i {
    top: 10px;
    right: 10px;
    position: absolute;
    font-size: 1.2rem;
  }

}

.actions {
  width: auto;
}

@media only screen and (max-width: 600px) {
  .actions {
    width: 100%;
  }
}

@media only screen and (max-width: 600px) {
  .logo {
    margin: 0 auto;
  }
  .rest-desc {
    width: 100%;
    text-align: center;
  }
}

.backlink-style {
  position: absolute;
  top: 0;
  left: 0;
  height: 64px;
  width: 100vw;
  background-color: black;
}

.header-move {
  padding-top: 64px !important;
}

.primary-text-color {
  color: #efa546 !important;
}

a {
  text-decoration: none;
}

.header-image {
  display: none;
}

.mobile-card {
  display: none;
}

.desktop-card {
  display: block;
}

@media only screen and (max-width: 600px) {
  .header-image {
    display: block;
  }

  .mobile-card {
    display: block;
  }

  .desktop-card {
    display: none;
  }
}

</style>



// WEBPACK FOOTER //
// src/components/webshop/Header.vue